import moment from "moment";
import { requirePermission } from "@/lib/permissions";
import { workDayLimitErrors } from "./bulk-upload-limits";
import { api } from "@/lib/api";
import { CatalogRepository } from "@/repositories/catalog.repository";
import { servicesByLowercaseName, type ServiceMatch } from "./bulk-upload-services";
import { DailyWorkRepository } from "@/repositories/daily-work.repository";
import { PropertyRepository } from "@/repositories/properties.repository";
import { DailyWorkScheduleRepository } from "@/repositories/daily-work-schedule.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { uuid } from "@/interfaces";
import { daily_work_status } from "@/models/daily-work.model";

const EXPECTED_HEADERS = ["property", "service", "subs", "payment", "date", "max_hours"];

export interface ParsedBulkRow {
  rowIndex: number;
  property: string; // resolved (carry-forward filled)
  service: string;
  subs: string;
  payment: string;
  date: string;
  max_hours: string;
}

export interface ParseResult {
  rows: ParsedBulkRow[];
  errors: string[];
}

/**
 * Parse TSV content. Headers normalized to lowercase. Empty property = carry forward from above.
 */
export function parseDailyWorkBulkTSV(content: string): ParseResult {
  const errors: string[] = [];
  const lines = content.trim().split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    return { rows: [], errors: ["File must have a header row and at least one data row."] };
  }

  const headerLine = lines[0];
  const headers = headerLine.split("\t").map((h) => h.trim().toLowerCase());
  const missing = EXPECTED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    errors.push(`Missing required columns: ${missing.join(", ")}. Expected: ${EXPECTED_HEADERS.join(", ")}.`);
  }

  const rows: ParsedBulkRow[] = [];
  let lastProperty = "";
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = line.split("\t").map((v) => (v ?? "").trim());
    const raw: Record<string, string> = {};
    headers.forEach((h, idx) => {
      raw[h] = values[idx] ?? "";
    });

    const property = (raw.property ?? "").trim() || lastProperty;
    if (i === 1 && !property) {
      errors.push(`Row ${i + 1}: Property is required for the first row.`);
    }
    if (property) lastProperty = property;

    const dateStr = raw.date ?? "";
    if (!dateStr) errors.push(`Row ${i + 1}: Date is required.`);
    else {
      const m = moment(dateStr, ["YYYY/MM/DD", "YYYY-MM-DD", "MM/DD/YYYY"], true);
      if (!m.isValid()) errors.push(`Row ${i + 1}: Invalid date "${dateStr}".`);
    }
    if (!(raw.service ?? "").trim()) errors.push(`Row ${i + 1}: Service is required.`);
    const maxHoursVal = (raw.max_hours ?? "").trim();
    if (maxHoursVal && isNaN(parseFloat(maxHoursVal))) {
      errors.push(`Row ${i + 1}: max_hours must be a number.`);
    }

    rows.push({
      rowIndex: i + 1,
      property,
      service: (raw.service ?? "").trim(),
      subs: (raw.subs ?? "").trim(),
      payment: (raw.payment ?? "").trim(),
      date: dateStr,
      max_hours: maxHoursVal,
    });
  }
  return { rows, errors };
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  resolved?: ResolvedRow[];
}

export interface ResolvedRow extends ParsedBulkRow {
  propertyId?: uuid;
  serviceId?: uuid;
  serviceParentId?: uuid | null; // for task template lookup (only templates for parent services)
  assignedOrgId?: uuid | null;
  assignedUserId?: uuid | null;
  workDate?: string; // YYYY-MM-DD
  tasksArray?: string[];
  paymentNumber?: number;
  maxHoursNumber?: number | null;
}

/**
 * Load all vendor properties into a map keyed by lowercase name. Used to avoid repeated DB calls during validation.
 */
async function loadVendorPropertyIdsMap(vendorOrgId: uuid): Promise<Map<string, { id: uuid }>> {
  const list = await PropertyRepository.servicedBy(vendorOrgId);
  const map = new Map<string, { id: uuid }>();
  for (const p of list) {
    const lower = p.name?.toLowerCase();
    if (lower) map.set(lower, { id: p.id as uuid });
  }
  return map;
}

async function loadVendorServicesMap(vendorOrgId: uuid): Promise<Map<string, ServiceMatch | null>> {
  return servicesByLowercaseName(await CatalogRepository.every({ vendor: [vendorOrgId] }));
}

/**
 * Resolve assignee (subs column): match org display_name or user name. Case-insensitive exact.
 * Organization (subs) is checked first, then individuals.
 */
function resolveAssignee(
  subsValue: string,
  subs: Array<{ id: uuid; name: string }>,
  individuals: Array<{ id: uuid; name: string }>
): { assignedOrgId?: uuid; assignedUserId?: uuid } | null {
  const lower = subsValue.toLowerCase().trim();
  if (!lower) return { assignedOrgId: undefined, assignedUserId: undefined };
  const sub = subs.find((s) => s.name?.toLowerCase() === lower);
  if (sub) return { assignedOrgId: sub.id, assignedUserId: undefined };
  const emp = individuals.find((e) => e.name?.toLowerCase() === lower);
  if (emp) return { assignedOrgId: undefined, assignedUserId: emp.id };
  return null;
}

async function loadVendorTaskTemplates(vendorOrgId: uuid): Promise<Map<uuid, string[]>> {
  const { items } = await api.taskTemplates.list({ actingOrganization: vendorOrgId });
  return new Map(items.map((template) => [template.service.id, template.tasks]));
}

/**
 * Validate all rows: resolve property, service, assignee. No DB writes. Return errors and optionally resolved rows.
 * Property and service lookups are cached per validation run to avoid repeated DB calls for the same names.
 */
export async function validateDailyWorkBulkUpload(
  parsedRows: ParsedBulkRow[],
  vendorOrgId: uuid
): Promise<ValidationResult> {
  await requirePermission("daily_work.view");
  const errors: string[] = [];
  const resolved: ResolvedRow[] = [];

  const [subs, individuals, propertyIdsMap, servicesMap, taskTemplates] = await Promise.all([
    IndividualsRepository.listSubsForAssignment(),
    IndividualsRepository.listForAssignment(),
    loadVendorPropertyIdsMap(vendorOrgId),
    loadVendorServicesMap(vendorOrgId),
    loadVendorTaskTemplates(vendorOrgId),
  ]);
  const subsList = subs.map((s) => ({ id: s.id, name: s.name }));
  const individualsList = individuals.map((e) => ({ id: e.id, name: e.name }));

  for (const row of parsedRows) {
    const out: ResolvedRow = { ...row };

    const prop = row.property ? propertyIdsMap.get(row.property.toLowerCase()) ?? null : null;
    if (row.property && !prop) {
      errors.push(`Row ${row.rowIndex}: Property "${row.property}" not found.`);
    } else if (prop) {
      out.propertyId = prop.id;
    }

    const svc = row.service ? servicesMap.get(row.service.toLowerCase()) ?? null : null;
    if (row.service && (svc === null || svc === undefined)) {
      errors.push(`Row ${row.rowIndex}: Service "${row.service}" not found.`);
    } else if (svc) {
      out.serviceId = svc.id;
      out.serviceParentId = svc.parent;
    }

    const assignee = resolveAssignee(row.subs, subsList, individualsList);
    if (row.subs.trim() && assignee === null) {
      errors.push(`Row ${row.rowIndex}: Subs "${row.subs}" not found.`);
    } else if (assignee) {
      out.assignedOrgId = assignee.assignedOrgId ?? null;
      out.assignedUserId = assignee.assignedUserId ?? null;
    } else {
      out.assignedOrgId = null;
      out.assignedUserId = null;
    }

    const workDate = moment(row.date, ["YYYY/MM/DD", "YYYY-MM-DD", "MM/DD/YYYY"]).format("YYYY-MM-DD");
    if (moment(workDate).isValid()) out.workDate = workDate;

    const paymentNum = row.payment.trim() ? parseFloat(row.payment) : null;
    if (row.payment.trim() && (paymentNum === null || isNaN(paymentNum))) {
      errors.push(`Row ${row.rowIndex}: Payment must be a number.`);
    } else if (paymentNum !== null) {
      out.paymentNumber = paymentNum;
    }

    out.maxHoursNumber = row.max_hours ? parseFloat(row.max_hours) : null;
    if (row.max_hours && (out.maxHoursNumber === null || isNaN(out.maxHoursNumber))) {
      errors.push(`Row ${row.rowIndex}: max_hours must be a number.`);
    }

    if (out.serviceId != null) {
      out.tasksArray = taskTemplates.get(out.serviceParentId ?? out.serviceId) ?? [];
    }

    errors.push(...workDayLimitErrors(row.rowIndex, { payment: out.paymentNumber ?? null, maxHours: out.maxHoursNumber ?? null, tasks: out.tasksArray ?? [] }));
    resolved.push(out);
  }

  return {
    valid: errors.length === 0,
    errors,
    resolved,
  };
}

export interface BulkUploadResult {
  dailyWorksCreated: number;
  dailyWorksReused: number;
  dailyWorkDaysCreated: number;
  errors: string[];
}

const REUSED_CONTRACT_STATUSES = ["active", "paused"] as const;

async function findExistingDailyWork(
  vendorOrgId: uuid,
  propertyId: uuid
): Promise<{ id: uuid; start_date: string | null; end_date: string | null } | null> {
  const newestPerStatus = await Promise.all(
    REUSED_CONTRACT_STATUSES.map(async (status) => {
      const { items } = await api.contracts.list({ actingOrganization: vendorOrgId, property: propertyId, status, sort: "createdAt", order: "desc", pageSize: 1 });
      return items[0];
    })
  );
  const [newest] = newestPerStatus.filter((contract) => contract !== undefined).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return newest ? { id: newest.id, start_date: newest.start, end_date: newest.end } : null;
}

/**
 * Run validation; if valid, reuse existing daily_work or create new ones, then create daily_work_days per row.
 */
export async function bulkUploadDailyWork(parsedRows: ParsedBulkRow[], vendorOrgId: uuid): Promise<BulkUploadResult> {
  await requirePermission("daily_work.create");

  const validation = await validateDailyWorkBulkUpload(parsedRows, vendorOrgId);
  if (!validation.valid || !validation.resolved) {
    return {
      dailyWorksCreated: 0,
      dailyWorksReused: 0,
      dailyWorkDaysCreated: 0,
      errors: validation.errors,
    };
  }

  const resolved = validation.resolved;
  const byPropertyId = new Map<string, ResolvedRow[]>();
  for (const r of resolved) {
    if (!r.propertyId) continue;
    const key = r.propertyId;
    const list = byPropertyId.get(key) ?? [];
    list.push(r);
    byPropertyId.set(key, list);
  }

  let dailyWorksCreated = 0;
  let dailyWorksReused = 0;
  let dailyWorkDaysCreated = 0;

  for (const [_propertyId, rows] of byPropertyId) {
    if (rows.length === 0) continue;
    const first = rows[0];
    if (!first.propertyId || !first.workDate) continue;

    const dates = rows.map((r) => r.workDate!).filter(Boolean);
    const uploadStartDate = dates.reduce((a, b) => (a < b ? a : b));
    const uploadEndDate = dates.reduce((a, b) => (a > b ? a : b));

    let dailyWorkId: uuid;

    const existing = await findExistingDailyWork(vendorOrgId, first.propertyId);
    if (existing) {
      dailyWorkId = existing.id;
      dailyWorksReused++;

      // Expand date range if the uploaded dates fall outside the existing range
      const newStartDate = !existing.start_date || uploadStartDate < existing.start_date ? uploadStartDate : existing.start_date;
      const newEndDate = !existing.end_date || uploadEndDate > existing.end_date ? uploadEndDate : existing.end_date;
      if (newStartDate !== existing.start_date || newEndDate !== existing.end_date) {
        await DailyWorkRepository.update(existing.id, {
          start_date: newStartDate,
          end_date: newEndDate,
        });
        // The wider window means the master schedule owes new generated days;
        // materialize them now instead of waiting for the nightly sync.
        try {
          await DailyWorkScheduleRepository.syncSchedule(existing.id);
        } catch (error) {
          console.error("Master schedule sync after bulk-upload date extension failed:", error);
        }
      }
    } else {
      const dailyWork = await DailyWorkRepository.create({
        vendor: vendorOrgId,
        property: first.propertyId,
        status: daily_work_status.active,
        start_date: uploadStartDate,
        end_date: uploadEndDate,
      });
      dailyWorkId = dailyWork.id;
      dailyWorksCreated++;
    }

    for (const r of rows) {
      if (!r.serviceId || !r.workDate) continue;
      await DailyWorkRepository.addDay(dailyWorkId, {
        date: r.workDate,
        service: r.serviceId,
        tasks: r.tasksArray ?? [],
        assignment: r.assignedOrgId ? { subcontractor: r.assignedOrgId } : r.assignedUserId ? { worker: r.assignedUserId } : null,
        ...((r.assignedOrgId || r.assignedUserId) && r.paymentNumber != null ? { rate: r.paymentNumber } : {}),
        ...(r.maxHoursNumber != null ? { maxHours: r.maxHoursNumber } : {}),
      });
      dailyWorkDaysCreated++;
    }
  }

  return {
    dailyWorksCreated,
    dailyWorksReused,
    dailyWorkDaysCreated,
    errors: [],
  };
}
